/**
 * A sub-section, as a card: a shaded header band naming the group, and what
 * belongs to it inside the border. See GroupCard.module.css for why.
 *
 * Use it for every sub-header under a page or stage title. Do not use it for
 * the page or stage title itself.
 */
import { useId, type ReactNode } from 'react';
import styles from './GroupCard.module.css';

export function GroupCard({
  title, meta, aside, id, level = 3, list = false, focusable = false, children,
}: {
  title: ReactNode;
  /** A quiet count or qualifier beside the title: "2 people". */
  meta?: ReactNode;
  /** Something at the far end of the band, such as a status. */
  aside?: ReactNode;
  /** The heading's id, when something links or jumps to it. */
  id?: string;
  level?: 2 | 3 | 4;
  /** The body is a list whose rows carry their own padding and separators. */
  list?: boolean;
  /** Lets a "go to" button put focus on the heading. */
  focusable?: boolean;
  children: ReactNode;
}) {
  const auto = useId();
  const headingId = id ?? auto;
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4';
  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <Heading id={headingId} className={styles.header} tabIndex={focusable ? -1 : undefined}>
        {title}
        {meta && <span className={styles.meta}>{meta}</span>}
        {aside && <span className={styles.aside}>{aside}</span>}
      </Heading>
      <div className={list ? styles.bodyList : styles.body}>{children}</div>
    </section>
  );
}
